/**
 * @author       david.loehr@zumper.com (David Loehr)
 * @copyright    Copyright (c) 2023, Zumper
 * @description  AboutLazyLoadableSection
 */

/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import { ScrollingNavSection } from '@zcode-ui/web-zumper-ui'
import { createLazyLoadableSection } from '@zcode/lazy-loadable-section'
import { ErrorFallback, LoadingFallback } from '@zcode/nr/es/shared/components/Fallback'
import React from 'react'
import type { ErrorBoundaryProps } from 'react-error-boundary'
import { useSelector } from 'react-redux'
import { logger } from '../../../logger'
import { selectEntity } from '../../../modules/detail/modules/entity/selectors/index'

const style = css`
  display: flex;
  align-items: center;
  justify-content: center;
`

const heightBase = css`
  height: 1032px;
  @media (min-width: 992px) {
    height: 698px;
  }
`

// to account for <AddressDetails /> added in the render method of About
const heightWithDetails = css`
  height: 1314px;
  @media (min-width: 992px) {
    height: 980px;
  }
`

const onRenderError: ErrorBoundaryProps['onError'] = (error, info) => {
  logger.error('render error for AboutLazyLoadableSection', info, error)
}

const onLoadingError = (error: unknown) => {
  logger.error('loading error for AboutLazyLoadableSection', error)
}

const loadFn = /* #__LOADABLE__ */ async () => await import('./About')

const message = `Sorry, we couldn't load the property details...`
const handleReload = async () => await AboutLazyLoadableSection.reload()

const AboutErrorFallback = () => (
  <ErrorFallback css={style} message={message} onClickReload={handleReload} />
)

const AboutLoadingFallback = () => {
  const entity = useSelector(selectEntity)
  const { isBuilding } = entity ?? {}
  const isSingleUnitListing = !isBuilding
  return (
    <React.Fragment>
      <ScrollingNavSection name="About">
        <LoadingFallback
          css={[style, isSingleUnitListing ? heightWithDetails : heightBase]}
          name="property details"
        />
      </ScrollingNavSection>
      <ScrollingNavSection name="ResidentQuotes" />
    </React.Fragment>
  )
}
const options = {
  errorFallback: AboutErrorFallback,
  loadingFallback: AboutLoadingFallback,
  onRenderError,
  onLoadingError,
}

export const AboutLazyLoadableSection = createLazyLoadableSection(loadFn, options)
